import React, { useState } from 'react'
import _ from 'lodash';
import { AgTechFormContext } from '@agtech/core/forms/AgTechFormContext';
import { AgTechClickEventHandler, AgTechButtonProps, AgTechButton } from '@agtech/web/components/Buttons/AgTechButtons';
import { AgTechFormNumberControl } from '@agtech/web/components/Forms/Controls/AgTechFormNumberControl';
import { AgTechFormReadOnlyTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormReadOnlyTextControl';
import { AgTechFormTextAreaControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextAreaControl';
import { AgTechFormTextControl } from '@agtech/web/components/Forms/Controls/AgTechFormTextControl';
import { AgTechFormToggleControl } from '@agtech/web/components/Forms/Controls/AgTechFormToggleControl';
import AgTechFormBody from '@agtech/web/components/Forms/Layout/AgTechFormBody';
import { AgTechFormRow } from '@agtech/web/components/Forms/Layout/AgTechFormRow';
import { AgTechFormSection } from '@agtech/web/components/Forms/Layout/AgTechFormSection';
import { DefaultSaleLot, SaleLot, getLotStatus } from '@sales/data/lots/LotData';
import { useLotDataWriteLotAction } from '@sales/data/lots/LotDataActions';
import { LotCategoryDropdown } from '../components/form/dropdowns/LotCategoryDropdown';
import { useLotDataGetSaleLotDataQuery } from '@sales/data/lots/LotDataQueries';
import { AgTechIndex } from '@agtech/core/utilities/AgTechUtilities';
import AgTechFormDialog from '@agtech/web/components/Portals/Dialogs/AgTechFormDialog';

export type LotDialogProps = {
    lotId?: number
}

type LotDialogModel = SaleLot & {
    bidderSoldTo?: any
}

const LotDialog = (props: LotDialogProps) => {
    const writeLotAction = useLotDataWriteLotAction();
    const lotDialogQuery = useLotDataGetSaleLotDataQuery();

    return (
        <AgTechFormDialog
            width={550}
            structure={() => ({
                header: props.lotId ? 'Update Lot' : 'Create Lot',
                submitButtonText: props.lotId ? 'Update Lot' : 'Create Lot'
            })}
            dialogData={{
                query: lotDialogQuery,
                getDialogData: lotData => getLotDialogModel(lotData, props.lotId)
            }}
            dialogAction={{
                action: writeLotAction,
                getActionData: d => {
                    return {
                        ...d,
                        bidder: d.bidderSoldTo
                    }
                }
            }}
        >
            {(formContext) => (
                <LotDialogBody formContext={formContext} />
            )}
        </AgTechFormDialog>
    )
};

const LotDialogBody = (props: { formContext: AgTechFormContext<LotDialogModel> }) => {
    const lotEntity = props.formContext.entity;
    const [groupSizeVisible, setGroupSizeVisibility] = useState<boolean>(lotEntity.groupSize !== undefined && lotEntity.groupSize > 0);

    return (
        <AgTechFormBody>
            <AgTechFormRow>
                <AgTechFormTextControl
                    entityType={lotEntity}
                    caption="Lot Number"
                    required={true}
                    focused={true}
                    value={entity => entity.lotNumber}
                    onControlValueChanged={async (e, val) => ({
                        ...e,
                        lotNumber: val
                    })}
                    validation={{
                        validateOnSubmit: (lot, val) => {
                            return val.length > 10 ? 'A lot number cannot exceed 10 characters in length' : '';
                        }
                    }}
                />
                <AgTechFormNumberControl
                    entityType={lotEntity}
                    caption="Sale Order"
                    value={entity => entity.saleOrder}
                    required={true}
                    onControlValueChanged={async (e, val) => ({
                        ...e,
                        saleOrder: val
                    })}
                />
            </AgTechFormRow>
            <AgTechFormRow>
                <AgTechFormTextAreaControl
                    entityType={lotEntity}
                    caption="Description"
                    value={entity => entity.lotDescription}
                    width={100}
                    rows={4}
                    onControlValueChanged={async (e, val) => ({
                        ...e,
                        lotDescription: val
                    })}
                />
            </AgTechFormRow>
            <AgTechFormRow>
                <LotCategoryDropdown
                    entityType={lotEntity}
                    caption='Category'
                    getSelectedLotCategoryId={lotEntity => lotEntity.lotCategoryId}
                    onLotCategorySelected={async (lot, value) => {
                        return ({
                            ...lot,
                            lotCategoryId: value?.id,
                        });
                    }}
                    width={65}
                />
                <AgTechFormReadOnlyTextControl
                    entityType={lotEntity}
                    caption="Status"
                    value={e => getLotStatus(e)}
                    controlStyles={{ width: 35 }}
                />
            </AgTechFormRow>
            <AgTechFormSection header='Group Information'>
                <AgTechFormRow>
                    <AgTechFormToggleControl
                        entityType={lotEntity}
                        caption='Is Group'
                        value={entity => groupSizeVisible}
                        onControlValueChanged={async (entity, value) => {
                            setGroupSizeVisibility(value);
                            return entity;
                        }}
                    />
                    <AgTechFormNumberControl
                        entityType={lotEntity}
                        caption='Size'
                        value={entity => entity.groupSize ?? 0 }
                        readonly={!groupSizeVisible}
                        onControlValueChanged={async (ent, val) => ({
                            ...ent,
                            groupSize: val
                        })}
                        />
                </AgTechFormRow>
            </AgTechFormSection>
        </AgTechFormBody>
    );
}

const getLotDialogModel = (lotData: { lots: AgTechIndex<SaleLot> }, lotId?: number) => {
    const lots = Object.values(lotData.lots);

    let nextSaleOrder = _.max(lots.map(lt => lt.saleOrder)) ?? 0;
    let nextLotNumber = _.max(lots.map(lt => parseInt(lt.lotNumber))) ?? 0;

    nextSaleOrder++;
    nextLotNumber++;

    const lotEntity = lotId ? lotData.lots[lotId] : {
        ...DefaultSaleLot,
        lotNumber: nextLotNumber.toString(),
        saleOrder: nextSaleOrder
    };

    const lotDialogModel: LotDialogModel = {
        ...lotEntity,
    };

    return lotDialogModel;
}

LotDialog.Open = (e: AgTechClickEventHandler, props: LotDialogProps) => {
    e.dialogs.openDialog({
        dialog: () => <LotDialog lotId={props.lotId} />
    });
}

LotDialog.Button = (props: LotDialogProps & AgTechButtonProps) => {
    let buttonProps: AgTechButtonProps = {
        text: props.lotId ? 'Update Lot' : 'Create New Lot',
        icon: props.lotId ? 'fas fa-edit' : 'fas fa-plus',
        ...props,
        action: e => LotDialog.Open(e, props)
    }

    return <AgTechButton {...buttonProps} />
}

export default LotDialog;